import { useSuspenseQuery } from '@apollo/client';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { match } from 'ts-pattern';

import { TranscriptTimestampDivider } from '@eluve/blocks';
import { Button, Stack, Text } from '@eluve/components';
import { useReviewHasuraContext } from '@eluve/session-helpers';
import { DynamicArtifactRenderer } from '@eluve/smart-blocks';

import { AppointmentAudio } from '../AppointmentAudio';
import { getReviewerAppointmentDetails } from '../operations';
import { useAppointmentParams } from '../useAppointmentParams';

import { GoldenSummaryContextProvider } from './GoldenSummaryContextProvider';
import { GoldenSummaryEditor } from './GoldenSummaryEditor';
import { GoldenSummarySelect } from './GoldenSummarySelect';
import { useSummary } from './useSummary';

type TAB_OPTIONS = 'TRANSCRIPT' | 'CORRECTED_TRANSCRIPT' | 'SUMMARY';

type TranscriptSegment = {
  transcript: string;
  recordingStartedAt: string;
};

export const GoldenSummaryContent: React.FC = () => {
  const { tenantId, appointmentId } = useAppointmentParams();
  const [activeTab, setActiveTab] = useState<TAB_OPTIONS>('TRANSCRIPT');
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const [correctedTranscription, setCorrectedTranscription] = useState('');
  const [transcriptSegments, setTranscriptSegments] = useState<
    TranscriptSegment[]
  >([]);
  const { llmSummary, originalContent } = useSummary({
    appointmentId,
  });
  const context = useReviewHasuraContext();

  const { data } = useSuspenseQuery(getReviewerAppointmentDetails, {
    variables: {
      tenantId,
      appointmentId,
    },
    context,
  });

  useEffect(() => {
    if (data?.appointmentsByPk?.segments) {
      const sortedSegments = data.appointmentsByPk.segments
        .slice()
        .sort((a, b) => {
          const aTime = new Date(a.recordingStartedAt ?? 0).getTime();
          const bTime = new Date(b.recordingStartedAt ?? 0).getTime();
          return aTime - bTime;
        })
        .map((s) => ({
          transcript: s.transcript || '',
          recordingStartedAt: s.recordingStartedAt || new Date().toISOString(),
        }));

      setTranscriptSegments(sortedSegments);

      const hasAnyCorrections = data.appointmentsByPk.segments.some((segment) =>
        segment.utterances?.some((utterance) => utterance.hasHumanUpdatedText),
      );

      if (hasAnyCorrections) {
        setCorrectedTranscription(
          data.appointmentsByPk.segments
            .flatMap((segment) =>
              segment.utterances
                .map((u) => u.speaker + ': ' + u.text)
                .join('\n'),
            )
            .join('\n\n'),
        );
      }
    }
  }, [data]);

  const handleNoteChange = (value: string) => {
    setActiveNoteId(value);
  };

  const footerPortalContent = useMemo(() => {
    return createPortal(
      <Stack
        className="fixed bottom-0 h-16 border-t bg-white"
        gap={2}
        direction="horizontal"
      >
        <AppointmentAudio appointmentId={appointmentId} tenantId={tenantId} />
      </Stack>,
      document.getElementById('portal-footer') || document.body,
    );
  }, [appointmentId, tenantId]);

  if (!llmSummary) {
    return (
      <Stack gap={2}>
        <Text>No summary available. Cannot create golden summary.</Text>
      </Stack>
    );
  }

  return (
    <Stack className="mt-4" gap={2}>
      <Stack gap={4} direction="horizontal" align="start">
        <div className="min-w-0 flex-1 rounded-lg border border-border-primary p-4">
          <Stack gap={2}>
            <Stack
              className="rounded-xl bg-bg-quaternary p-2"
              gap={2}
              direction="horizontal"
              justify="between"
            >
              <Button
                onClick={() => {
                  if (activeTab === 'TRANSCRIPT') return;
                  setActiveTab('TRANSCRIPT');
                }}
                variant={activeTab === 'TRANSCRIPT' ? 'default' : 'outline'}
                className="min-w-0 grow basis-0 truncate"
              >
                Transcript
              </Button>
              {correctedTranscription && (
                <Button
                  onClick={() => {
                    if (activeTab === 'CORRECTED_TRANSCRIPT') return;
                    setActiveTab('CORRECTED_TRANSCRIPT');
                  }}
                  variant={
                    activeTab === 'CORRECTED_TRANSCRIPT' ? 'default' : 'outline'
                  }
                  className="min-w-0 grow basis-0 truncate"
                >
                  Corrected Transcript
                </Button>
              )}
              <Button
                onClick={() => {
                  if (activeTab === 'SUMMARY') return;
                  setActiveTab('SUMMARY');
                }}
                variant={activeTab === 'SUMMARY' ? 'default' : 'outline'}
                className="min-w-0 grow basis-0 truncate"
              >
                Original Summary
              </Button>
            </Stack>
            <Stack
              className="scrollbar-thin scrollbar-track-bg-quaternary scrollbar-thumb-black scrollbar-track-rounded-full scrollbar-thumb-rounded-full mt-1 max-h-[80svh] overflow-y-scroll pr-4"
              gap={2}
            >
              {match(activeTab)
                .with('TRANSCRIPT', () => (
                  <Stack gap={4}>
                    {transcriptSegments.map((segment, index) => (
                      <Fragment key={index}>
                        <TranscriptTimestampDivider
                          timestamp={segment.recordingStartedAt}
                          action="started"
                          dateFormat={undefined}
                        />
                        <Text className="leading-loose whitespace-pre-line">
                          {segment.transcript}
                        </Text>
                      </Fragment>
                    ))}
                  </Stack>
                ))
                .with('CORRECTED_TRANSCRIPT', () => (
                  <Text className="leading-loose whitespace-pre-line">
                    {correctedTranscription}
                  </Text>
                ))
                .otherwise(() => {
                  return (
                    <DynamicArtifactRenderer
                      isReadonly={true}
                      blocks={llmSummary.blocks}
                    />
                  );
                })}
            </Stack>
          </Stack>
        </div>

        <div className="min-w-0 flex-1 rounded-lg border border-border-primary p-4">
          <Stack gap={2}>
            <GoldenSummaryContextProvider>
              <GoldenSummarySelect
                onSelectSummary={handleNoteChange}
                originalContent={originalContent}
              />
              {!activeNoteId ? (
                <Stack
                  gap={4}
                  className="py-8"
                  fullHeight
                  align="center"
                  justify="center"
                >
                  <Text>
                    Select an existing golden summary or create a new one.
                  </Text>
                </Stack>
              ) : (
                <Stack
                  className="scrollbar-thin scrollbar-track-bg-quaternary scrollbar-thumb-black scrollbar-track-rounded-full scrollbar-thumb-rounded-full my-1 max-h-[80svh] overflow-y-scroll pr-4"
                  gap={2}
                >
                  <GoldenSummaryEditor
                    key={activeNoteId}
                    tenantId={tenantId}
                    appointmentId={appointmentId}
                    activeNoteId={activeNoteId}
                  />
                </Stack>
              )}
            </GoldenSummaryContextProvider>
          </Stack>
        </div>
      </Stack>
      {footerPortalContent}
    </Stack>
  );
};
